import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Award,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';
import QuillEditorWithImageResize from './QuillEditorWithImageResize';
import SubjectiveTextAnswerArea from './SubjectiveTextAnswerArea';
import ScratchResultDetail from './ScratchResultDetail';
import PythonCodingResultDetail from './PythonCodingResultDetail';
import CppCodingResultDetail from './CppCodingResultDetail';
import SubjectiveTextResultDetail from './SubjectiveTextResultDetail';

interface Question {
  id: string;
  type: string;
  title?: string; // 题目名称（所有题型统一）
  content?: string; // 题干内容（富文本）
  score: number;
  referenceAnswer?: string; // 参考答案HTML
  scoringPoints?: string; // 评分规则HTML
  userAnswer?: string;
}

type DisplayMode = 'answer' | 'preview' | 'detail' | 'edit';

interface SubjectiveQuestionCardProps {
  question: Question;
  paperId?: string;
  mode: DisplayMode;
  answer?: string;
  teacherFeedback?: { score?: number; feedback?: string };
  onTextAnswerChange?: (questionId: string, text: string) => void;
  onSaveAnswer?: (question: Question) => void;
  isSavingAnswer?: boolean;
  // 编辑模式回调
  onContentChange?: (content: string) => void;
  onReferenceAnswerChange?: (referenceAnswer: string) => void;
  onScoringPointsChange?: (scoringPoints: string) => void;
}

export default function SubjectiveQuestionCard({
  question,
  paperId,
  mode,
  answer,
  teacherFeedback,
  onTextAnswerChange,
  onSaveAnswer,
  isSavingAnswer,
  onContentChange,
  onReferenceAnswerChange,
  onScoringPointsChange,
}: SubjectiveQuestionCardProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stem: true,
    answer: true,
    reference: false,
    rules: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // 判断是否显示参考答案和评分规则
  // detail/edit 模式显示，answer/preview 模式隐藏
  const showReferenceAndRules = mode === 'detail' || mode === 'edit';

  // 判断是否为编程题类型
  const isCodingType = ['scratch_coding', 'python_coding', 'cpp_coding'].includes(question.type);
  const isTextSubjective = question.type === 'subjective' || question.type === 'coding';

  // 渲染答题区
  const renderAnswerArea = () => {
    if (mode === 'detail') {
      // 详情模式：根据题型渲染对应的 ResultDetail 组件
      if (question.type === 'scratch_coding') {
        return (
          <ScratchResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else if (question.type === 'python_coding') {
        return (
          <PythonCodingResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else if (question.type === 'cpp_coding') {
        return (
          <CppCodingResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else {
        // 文字主观题
        return (
          <SubjectiveTextResultDetail
            question={question}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      }
    } else if (mode === 'answer' || mode === 'preview') {
      // 答题/预览模式：文字主观题显示输入框
      if (isTextSubjective && onTextAnswerChange && onSaveAnswer) {
        return (
          <SubjectiveTextAnswerArea
            question={question}
            answer={answer || ''}
            isRevealed={mode === 'preview'}
            isSaving={isSavingAnswer}
            onTextAnswerChange={onTextAnswerChange}
            onSaveAnswer={onSaveAnswer}
          />
        );
      }
      // 编程题在答题模式下不显示答案区（由专门的编辑器处理）
      return null;
    } else if (mode === 'edit') {
      // 编辑模式：显示提示
      return (
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-800">
            编辑模式下，请在题目管理页面修改题目内容和答案
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* 2. 题干区 - 仅编辑模式显示（quiz 预览/答题模式下题干由外部 QuizQuestionTitle 统一渲染） */}
      {mode === 'edit' && question.content && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('stem')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-indigo-600" />
              <span className="font-medium text-gray-900">题目内容</span>
            </div>
            {expandedSections.stem ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.stem && (
            <div className="p-4">
              {mode === 'edit' && onContentChange ? (
                <QuillEditorWithImageResize
                  value={question.content || ''}
                  onChange={onContentChange}
                  questionId={question.id}
                  targetField="content"
                  placeholder="请输入题目内容..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.content}
                />
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* 3. 答题区 */}
      {renderAnswerArea()}

      {/* 4. 参考答案区（仅 detail/edit 模式显示） */}
      {showReferenceAndRules && question.referenceAnswer && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('reference')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">参考答案</span>
            </div>
            {expandedSections.reference ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.reference && (
            <div className="p-4">
              {mode === 'edit' && onReferenceAnswerChange ? (
                <QuillEditorWithImageResize
                  value={question.referenceAnswer || ''}
                  onChange={onReferenceAnswerChange}
                  questionId={question.id}
                  targetField="referenceAnswer"
                  placeholder="请输入参考答案..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.referenceAnswer || '<p>暂无参考答案说明</p>'}
                />
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* 5. 评分规则区（仅 detail/edit 模式显示） */}
      {showReferenceAndRules && question.scoringPoints && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('rules')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-gray-900">评分规则</span>
            </div>
            {expandedSections.rules ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.rules && (
            <div className="p-4">
              {mode === 'edit' && onScoringPointsChange ? (
                <QuillEditorWithImageResize
                  value={question.scoringPoints || ''}
                  onChange={onScoringPointsChange}
                  questionId={question.id}
                  targetField="scoringPoints"
                  placeholder="请输入评分规则..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.scoringPoints || '<p>暂无评分规则说明</p>'}
                />
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
